/**
 * Canvas HUD orchestration (DESIGN 6.2 layer 9, 6.5): owns the queue bar
 * (hudQueue.ts) and the panels (hudPanels.ts), routes the HUD-relevant
 * events (local casts, magicks, queue full, self-shock, wave changes,
 * friendly fire on allies) and draws everything in screen space at
 * `hudScale = clamp(h / 900, 0.75, 1.4)`.
 */

import { ARENA, MAGICKS, MODES, type Arena, type CastMode, type GameEvent } from '@shared/index.js';
import { drawCtfBar, drawMinimap, drawScoreboard } from './pvpHud.js';
import type { ViewModel } from '../state/viewModel.js';
import { TEAMS, UI } from '../palette.js';
import type { Ctx } from './gfx.js';
import { QueueHud } from './hudQueue.js';
import { DepthHud } from './hudDepth.js';
import { discoveries, ownFind } from '../state/discoveries.js';
import { GRAND_MAGICKS, elementName, recipeOf } from '@shared/index.js';
import { Panels } from './hudPanels.js';
import { Captions } from './story.js';

export function hudScale(h: number): number {
  return Math.min(1.4, Math.max(0.75, h / 900));
}

export class Hud {
  readonly queue = new QueueHud();
  readonly panels = new Panels();
  /** Deeper spells: the stash pods and the weave and Grand Magick pills. */
  readonly depth = new DepthHud();
  /** Story mode: the Book's lines. */
  readonly captions = new Captions();
  /** Phone layout: the touch buttons own the right quarter of the screen. */
  touch = false;
  /** Cast mode the modifiers currently imply (Shift -> area); set by the renderer from key state. */
  heldMode: CastMode = 'force';
  /** The map being played, for the minimap (capture the flag). */
  private arena: Arena = ARENA;

  setArena(arena: Arena): void {
    this.arena = arena;
  }

  onEvents(events: readonly GameEvent[], vm: ViewModel, localPid: number): void {
    for (const ev of events) {
      switch (ev.e) {
        case 'cast':
          if (ev.p === localPid && ev.kind !== 'none') this.queue.onLocalCast();
          break;
        case 'magick': {
          if (ev.p === localPid) this.queue.onLocalCast();
          const caster = vm.players.find((p) => p.id === ev.p);
          this.panels.banner(MAGICKS[ev.id].name, caster ? `cast by ${caster.name}` : '', UI.gold, 1.8);
          break;
        }
        case 'queue_full':
          if (ev.p === localPid) this.queue.shake();
          break;
        case 'weave':
          if (ev.p === localPid && ev.o === 'fail') this.queue.shake();
          break;
        case 'stash':
          if (ev.p === localPid) this.depth.flashPod(ev.s);
          if (ev.p === localPid && (ev.o === 'nothing' || ev.o === 'full')) this.queue.shake();
          break;
        case 'woven': {
          // Each wizard's book holds only their own finds, even in a shared room (discoveries.ts ownFind)
          if (!ownFind(ev, localPid)) break;
          if (discoveries.addPiece(ev.el)) {
            const r = recipeOf(ev.el);
            this.panels.banner(`NEW: ${elementName(ev.el).toUpperCase()}`, r ? `${elementName(r.a)} + ${elementName(r.b)}` : '', UI.gold, 2.4);
          }
          break;
        }
        case 'grand': {
          if (ev.o !== 'cast') break;
          if (ev.p === localPid) this.queue.onLocalCast();
          const caster = vm.players.find((p) => p.id === ev.p);
          if (ownFind(ev, localPid)) discoveries.addGrand(ev.id);
          this.panels.banner(GRAND_MAGICKS[ev.id].name.toUpperCase(), caster ? `cast by ${caster.name}` : '', UI.gold, 2.4);
          break;
        }
        case 'shock_self':
          if (ev.p === localPid) this.queue.wet();
          break;
        case 'flag': {
          const mine = vm.players.find((p) => p.isLocal)?.tm;
          const who = vm.players.find((p) => p.id === ev.by)?.name ?? 'Someone';
          const side = TEAMS[ev.team];
          const ours = mine === ev.team;
          switch (ev.act) {
            case 'take': this.panels.banner(ours ? 'YOUR FLAG IS TAKEN' : `${side.name.toUpperCase()} FLAG TAKEN`, `by ${who}`, ours ? UI.hpRed : side.color, 1.8); break;
            case 'drop': this.panels.banner(`${side.name.toUpperCase()} FLAG DROPPED`, ours ? 'Touch it to send it home' : 'Pick it up', side.color, 1.6); break;
            case 'return': this.panels.banner(`${side.name.toUpperCase()} FLAG HOME`, ev.by >= 0 ? `returned by ${who}` : 'it went home by itself', side.color, 1.4); break;
            case 'capture': {
              const scorer = vm.players.find((p) => p.id === ev.by)?.tm;
              this.panels.banner(scorer !== undefined ? `${TEAMS[scorer].name.toUpperCase()} SCORES` : 'CAPTURE', `${who} brought the ${side.name} flag home`, scorer !== undefined ? TEAMS[scorer].color : UI.gold, 2.2);
              break;
            }
          }
          break;
        }
        case 'wave': {
          const story = vm.room?.mode === 'story';
          if (vm.room?.mode === 'ctf') {
            if (ev.phase === 'countdown') this.panels.banner('GET READY', 'Bring their flag home while yours is home', UI.gold, 2);
            else if (ev.phase === 'match') this.panels.banner('CAPTURE THE FLAG', 'First to 3 captures', UI.text, 2.4);
            else if (ev.phase === 'gameover') this.panels.banner('MATCH OVER', '', UI.gold, 3);
            break;
          }
          switch (ev.phase) {
            case 'countdown': this.panels.banner('GET READY', story ? MODES.story.blurb : 'Wave 1 incoming', UI.gold, 2); break;
            case 'wave': this.panels.banner(`WAVE ${ev.n}`, waveLesson(ev.n), UI.text, 2.4); break;
            case 'breather': this.panels.banner('WAVE CLEAR', 'Dead allies return next wave', UI.hpGreen, 2.2); break;
            case 'story': {
              const [act, name] = actParts(vm);
              this.panels.banner(act.toUpperCase(), name, UI.gold, 2.6);
              break;
            }
            case 'gameover':
              if (story) this.panels.banner('ACT OVER', actParts(vm)[1], UI.gold, 4);
              else this.panels.banner('GAME OVER', `Reached wave ${ev.n}`, UI.hpRed, 4);
              break;
            case 'lobby':
              this.captions.clear();
              this.panels.banner('LOBBY', story ? 'Walk the Gatehouse while you wait' : 'Practise on the dummies', UI.muted, 2);
              break;
          }
          break;
        }
        case 'say':
          this.captions.say(ev.text);
          break;
        case 'checkpoint':
          this.panels.banner('RUNE STONE', 'You will stand up here', UI.text, 1.8);
          break;
        case 'ff':
          if (ev.dmg >= 200 && vm.players.some((p) => p.id === ev.tgt)) this.panels.flashAlly(ev.tgt);
          break;
        default: break;
      }
    }
  }

  step(dt: number, vm: ViewModel): void {
    this.queue.update(vm.me, dt);
    this.depth.step(dt);
    this.panels.step(dt);
    this.captions.step(dt);
  }

  /** `fps` is the renderer's own estimate, used when the view model has none yet. */
  draw(ctx: Ctx, vm: ViewModel, w: number, h: number, time: number, fps: number): void {
    const s = hudScale(h);
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    this.panels.drawWave(ctx, vm, s, w, this.touch);
    this.panels.drawBossBar(ctx, vm, w, s);
    if (vm.room?.mode === 'ctf') {
      drawCtfBar(ctx, vm, w, s);
      const bottom = drawScoreboard(ctx, vm, w, s);
      drawMinimap(ctx, vm, this.arena, w, bottom, s, this.touch);
    } else {
      this.panels.drawAllies(ctx, vm, w, s, time);
    }
    if (vm.me) {
      this.panels.drawMagickHint(ctx, vm.me, w, h, s, this.touch);
      this.queue.draw(ctx, w, h, vm.me, this.heldMode, s, time);
      const me = vm.me;
      const magickPill = me.magickHint.some((m) => MAGICKS[m].seq.length === me.queue.length);
      this.depth.draw(ctx, me, w, h, s, time, this.touch, (el) => discoveries.knows(el), magickPill);
    }
    this.panels.drawCenter(ctx, vm, vm.me, w, h, s, time);
    this.panels.drawBanners(ctx, w, h, s);
    // the Book's line sits above the queue bar (on a phone, left of the touch buttons)
    if (this.touch) this.captions.draw(ctx, w * 0.37, h - 150 * s, w * 0.7, s);
    else this.captions.draw(ctx, w / 2, h - 150 * s, w, s);
    this.panels.drawPingFps(ctx, vm, w, h, s, fps);
  }
}

/** "Act 1: The Drowned Cloister" -> ["Act 1", "The Drowned Cloister"]. */
function actParts(vm: ViewModel): [string, string] {
  const a = vm.story?.a ?? 'Act 1: The Drowned Cloister';
  const i = a.indexOf(': ');
  return i < 0 ? ['Story', a] : [a.slice(0, i), a.slice(i + 2)];
}

/** One-line lesson per scripted wave (DESIGN 4.4 "Lesson" column), shown under the wave banner. */
function waveLesson(n: number): string {
  switch (n) {
    case 1: return 'Spiders swarm: try A then Shift+LMB';
    case 2: return 'Beams and sprays: S or Q F with LMB';
    case 3: return 'Rangers kite: E + LMB stops arrows';
    case 4: return 'Bombers chain: one fireball starts it';
    case 5: return 'Shamans wet you: no Lightning while wet';
    case 6: return 'Burn the troll or it regenerates';
    case 7: return 'Tree spirits: fire works, water does not';
    case 8: return 'Skeletons: Life hurts, Arcane heals them';
    case 9: return 'Orcs: lightning vs armor; wet + cold shatters';
    case 10: return 'Mixed company';
    case 11: return 'Mixed company, more of it';
    case 12: return 'Jotunn is immune to lightning';
    default: return n > 12 ? 'Endless: they only get tougher' : '';
  }
}
